/**
 * Seguimiento de la posicion con watchPosition.
 *
 * Funciona solo con la aplicacion en primer plano, que es la decision de
 * arquitectura cerrada en la seccion 7 de la especificacion. Aqui se asume y se
 * gestiona: al volver del segundo plano se reanuda el seguimiento, porque el
 * navegador puede haberlo suspendido mientras tanto.
 *
 * La posicion no se guarda en ningun sitio: vive en memoria mientras dura la
 * sesion y se envia al servidor solo al registrar una visita.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type EstadoUbicacion =
  | 'inactiva'
  | 'solicitando'
  | 'activa'
  | 'denegada'
  | 'no_disponible'
  | 'error';

export interface PosicionActual {
  lat: number;
  lon: number;
  precision: number;
  momento: number;
}

interface Resultado {
  estado: EstadoUbicacion;
  posicion: PosicionActual | null;
  activar: () => void;
  detener: () => void;
  soportada: boolean;
}

const OPCIONES: PositionOptions = {
  enableHighAccuracy: true,
  // Sin cache: una posicion de hace un minuto puede estar a varias calles.
  maximumAge: 0,
  timeout: 20_000,
};

export function useGeolocalizacion(): Resultado {
  const soportada = typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const [estado, setEstado] = useState<EstadoUbicacion>('inactiva');
  const [posicion, setPosicion] = useState<PosicionActual | null>(null);
  const vigilante = useRef<number | null>(null);
  const quiereSeguir = useRef(false);

  const detener = useCallback(() => {
    quiereSeguir.current = false;
    if (vigilante.current !== null) {
      navigator.geolocation.clearWatch(vigilante.current);
      vigilante.current = null;
    }
    setEstado('inactiva');
  }, []);

  const arrancarVigilancia = useCallback(() => {
    if (!soportada || vigilante.current !== null) return;

    setEstado((anterior) => (anterior === 'activa' ? anterior : 'solicitando'));

    vigilante.current = navigator.geolocation.watchPosition(
      (lectura) => {
        setPosicion({
          lat: lectura.coords.latitude,
          lon: lectura.coords.longitude,
          precision: lectura.coords.accuracy,
          momento: lectura.timestamp,
        });
        setEstado('activa');
      },
      (fallo) => {
        if (fallo.code === fallo.PERMISSION_DENIED) {
          quiereSeguir.current = false;
          setEstado('denegada');
        } else if (fallo.code === fallo.POSITION_UNAVAILABLE) {
          setEstado('no_disponible');
        } else {
          // Un tiempo de espera agotado no es definitivo: en un patio interior
          // o en un tunel se recupera solo en cuanto hay senal.
          setEstado('error');
        }
      },
      OPCIONES,
    );
  }, [soportada]);

  const activar = useCallback(() => {
    quiereSeguir.current = true;
    arrancarVigilancia();
  }, [arrancarVigilancia]);

  /**
   * Al volver a primer plano se rearranca la vigilancia. Algunos navegadores
   * moviles la suspenden al pasar a segundo plano y no la reanudan solos, con
   * lo que la aplicacion se quedaria con una posicion congelada.
   */
  useEffect(() => {
    function alCambiarVisibilidad(): void {
      if (document.visibilityState !== 'visible' || !quiereSeguir.current) return;
      if (vigilante.current !== null) {
        navigator.geolocation.clearWatch(vigilante.current);
        vigilante.current = null;
      }
      arrancarVigilancia();
    }

    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    return () => {
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    };
  }, [arrancarVigilancia]);

  useEffect(() => {
    return () => {
      if (vigilante.current !== null) {
        navigator.geolocation.clearWatch(vigilante.current);
        vigilante.current = null;
      }
    };
  }, []);

  return {
    estado: soportada ? estado : 'no_disponible',
    posicion,
    activar,
    detener,
    soportada,
  };
}
